import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

// Shared "watched rodeos" store so the ★ Watch button stays in sync everywhere
// (mobile card rows, Info dialog, full draw sheet). Mirrors useFollowing's pattern.
let cache = null;            // Set of rodeo ids, or null before first load
let mutated = false;
const listeners = new Set();
function broadcast(set) { cache = set; listeners.forEach((fn) => fn(new Set(set))); }

export default function useWatchlist() {
  const [watched, setWatched] = useState(cache ? new Set(cache) : new Set());
  useEffect(() => {
    const fn = (s) => setWatched(s);
    listeners.add(fn);
    if (cache === null) {
      cache = new Set();
      api.get("/watchlist")
        .then((r) => { if (!mutated) broadcast(new Set((r.data || []).map((w) => w.rodeo_id))); })
        .catch(() => { if (!mutated) broadcast(new Set()); });
    } else {
      setWatched(new Set(cache));
    }
    return () => listeners.delete(fn);
  }, []);

  const isWatched = useCallback((id) => (cache || new Set()).has(id), [watched]);

  const toggle = useCallback(async (id, name) => {
    const on = (cache || new Set()).has(id);
    mutated = true;
    const next = new Set(cache || new Set());
    on ? next.delete(id) : next.add(id);
    broadcast(next);
    try {
      if (on) { await api.delete(`/watchlist/${id}`); }
      else { await api.post(`/watchlist/${id}`); toast.success(`Watching ${name || "rodeo"} — we'll remind you before entries close`); }
    } catch {
      const revert = new Set(cache || new Set());
      on ? revert.add(id) : revert.delete(id);
      broadcast(revert);
      toast.error("Couldn't update your watchlist — try again");
    }
  }, []);

  return { watched, isWatched, toggle };
}
